/**
 * Foto del documento (D-03) e caricamento dei documenti della scheda: regole senza dipendenze dal
 * browser. Le usano componenti/FotoDocumento.tsx e servizi/documenti.ts; la prova
 * apps/api/test/elenco-foto.test.ts le esegue contro l'API vera, come regole-anagrafica.ts.
 */

/** Lato lungo massimo della foto ridotta, in pixel (D-03). */
export const LATO_LUNGO_MASSIMO = 1600

/** Qualita' JPEG della foto ridotta, da 0 a 1 come la vuole canvas.toBlob (D-03: 0,7). */
export const QUALITA_JPEG = 0.7

/** Formato della foto ridotta: lo stesso su ogni dispositivo, qualunque sia il file della fotocamera. */
export const MIME_FOTO = 'image/jpeg'

/**
 * Dimensioni della foto ridotta: il lato lungo scende a `latoMassimo` e l'altro in proporzione. Una
 * foto gia' piu' piccola non si ingrandisce: si ricodifica soltanto, e perde comunque i metadati.
 */
export function dimensioniRidotte(
  larghezza: number, altezza: number, latoMassimo = LATO_LUNGO_MASSIMO,
): { larghezza: number; altezza: number } {
  if (!Number.isFinite(larghezza) || !Number.isFinite(altezza) || larghezza <= 0 || altezza <= 0) {
    throw new RangeError('Dimensioni della foto non valide.')
  }
  const fattore = Math.min(1, latoMassimo / Math.max(larghezza, altezza))
  return {
    larghezza: Math.max(1, Math.round(larghezza * fattore)),
    altezza: Math.max(1, Math.round(altezza * fattore)),
  }
}

const due = (n: number) => String(n).padStart(2, '0')

/**
 * Nome del file della foto. Il nome dato dalla fotocamera («IMG_2034.JPG», «image.jpg») non dice
 * nulla a chi apre l'elenco: si usano il tipo di documento, la data e l'ora locali. In archivio il
 * nome si conserva cifrato con la chiave della persona (rotte/documenti.ts).
 */
export function nomeFoto(tipo: string, adesso = new Date()): string {
  const parte = tipo.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'documento'
  const giorno = `${adesso.getFullYear()}-${due(adesso.getMonth() + 1)}-${due(adesso.getDate())}`
  return `foto-${parte}-${giorno}-${due(adesso.getHours())}${due(adesso.getMinutes())}.jpg`
}

/**
 * Nome del PDF del documento letto con «Compila dal documento» e allegato dopo il salvataggio: come
 * quello della foto, con «documento» e l'estensione del PDF.
 */
export function nomePdf(tipo: string, adesso = new Date()): string {
  return nomeFoto(tipo, adesso).replace(/^foto-/, 'documento-').replace(/\.jpg$/, '.pdf')
}

/**
 * Tipi MIME ricavati dall'estensione, solo per i tipi che l'API accetta (MIME_AMMESSI in
 * apps/api/src/servizi/archivio.ts; la prova verifica che coincidano).
 */
export const MIME_PER_ESTENSIONE: Readonly<Record<string, string>> = {
  pdf: 'application/pdf',
  jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', heic: 'image/heic', webp: 'image/webp',
  tif: 'image/tiff', tiff: 'image/tiff',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  odt: 'application/vnd.oasis.opendocument.text',
  ods: 'application/vnd.oasis.opendocument.spreadsheet',
  txt: 'text/plain', csv: 'text/csv', eml: 'message/rfc822',
}

/**
 * Tipo MIME di un file scelto con «Scegli un file». Alcuni browser lasciano vuoto `File.type` (un
 * HEIC su Windows, un'estensione senza associazione nel sistema): prima si inviava
 * application/octet-stream e l'API rispondeva 415 anche per un PDF. Il tipo dichiarato dal browser,
 * se c'e', prevale; un'estensione sconosciuta resta application/octet-stream e l'API la rifiuta.
 */
export function mimeDelFile(nome: string, dichiarato: string): string {
  if (dichiarato) return dichiarato
  const estensione = /\.([a-z0-9]+)$/i.exec(nome)?.[1]?.toLowerCase()
  return (estensione && MIME_PER_ESTENSIONE[estensione]) || 'application/octet-stream'
}

/**
 * Corpo di POST /persone/:id/documenti (prenotazione). La classe del dato resta quella predefinita
 * dall'API, `personale`: un documento di identita' non e' un dato particolare.
 */
export function corpoPrenotazione(documento: { nome: string; mime: string; dimensione: number; tipo: string }) {
  return { nome: documento.nome, mime: documento.mime, dimensione: documento.dimensione, tipo: documento.tipo }
}

/** Peso di un file nell'anteprima: «640 kB», «1,2 MB». */
export function pesoLeggibile(byte: number): string {
  if (byte < 1024 * 1024) return `${Math.max(1, Math.round(byte / 1024))} kB`
  return `${(byte / (1024 * 1024)).toLocaleString('it-IT', { maximumFractionDigits: 1 })} MB`
}
